/* eslint-disable no-template-curly-in-string */
// 灵感 发现/关注/画板导航
import useAppStore from "@/context/store";
import { getUserInspirationBoards } from "@/services/discovery";
import { useSafeState } from "ahooks";
import { Tooltip } from "antd";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { When } from "react-if";
import HBIcon from "../HBIcon";
import HBSwiper from "../HBSwiper";
import styles from "./index.module.less";

export default function HBBoardNav({
  onChange,
  value,
  allText = "最新",
  extraOption,
  hideAll = false,
  baseHref,
}) {
  const { user } = useAppStore(({ user }) => ({ user }));
  const [activeOption, setActiveOption] = useSafeState(null);
  const [boards, setBoards] = useSafeState([]);
  const [loading, setLoading] = useSafeState(true);
  const lastUserRef = useRef(null);
  const swiperRef = useRef(null);

  // 获取用户灵感画板数据 - 只在用户变化时执行
  useEffect(() => {
    const fetchUserInspirationBoards = async () => {
      const currentUserId = user?.user_id;

      // 如果用户没有变化，则不重新加载
      if (currentUserId === lastUserRef.current) {
        return;
      }

      lastUserRef.current = currentUserId;

      if (!currentUserId) {
        setBoards([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await getUserInspirationBoards();

        if (result.boards && result.boards.length > 0) {
          // 使用新的 API，直接返回用户设置在发现页显示的画板
          const discoveryBoards = result.boards
            .filter((board) => board.is_private <= 0) // 过滤掉私有画板
            .map((board) => ({
              value: board.board_id.toString(),
              name: board.title,
              board_id: board.board_id,
              user_id: board.user_id,
              pin_count: board.pin_count,
              follow_count: board.follow_count,
              like_count: board.like_count,
              is_private: board.is_private,
              seq: board.seq,
              pins: board.pins || [], // 画板中的采集列表
            }));

          setBoards(discoveryBoards);
        } else {
          setBoards([]);
        }
      } catch (error) {
        console.error("Failed to fetch user inspiration boards:", error);
        setBoards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInspirationBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => {
    if (value === null) {
      setActiveOption(null);
    } else if (extraOption?.value && value === extraOption?.value) {
      setActiveOption({ value: extraOption?.value, name: extraOption?.text });
    } else if (value && boards.length > 0) {
      const activeBoardOption = boards.find((option) => option.value === value);
      setActiveOption(activeBoardOption);
    } else {
      setActiveOption(undefined);
    }
  }, [value, boards, setActiveOption, extraOption]);

  const onClick = (evt, option) => {
    evt.preventDefault();
    setActiveOption(option);
    onChange && onChange(option);
  };

  const renderItem = (option, isExtra = false) => {
    const displayText = option?.name || option?.text || allText;
    const isBoard = option && option.board_id && !isExtra; // 判断是否为用户画板（有board_id的才是画板）

    const itemElement = (
      <a
        key={option?.value || "all"}
        data-gd-click="button_click"
        data-button-name="用户画板"
        data-module-name="页面切换tab"
        data-subject-name="按钮"
        data-board-id={option?.board_id}
        className={classNames([
          styles.item,
          isBoard ? styles.boardItem : null, // 为画板添加特殊样式
          (
            isExtra
              ? value === option?.value
              : activeOption?.value === option?.value
          )
            ? styles.active
            : null,
        ])}
        {...((
          isExtra
            ? value === option?.value
            : activeOption?.value === option?.value
        )
          ? { id: "active-filter-item" }
          : {})}
        {...(baseHref && option?.value
          ? {
              href: isExtra
                ? baseHref.replace("discovery", option?.value)
                : baseHref + "/" + option?.value,
            }
          : baseHref
          ? { href: baseHref }
          : null)}
        onClick={(evt) =>
          onClick(
            evt,
            isExtra ? { value: option?.value, name: option?.text } : option
          )
        }
      >
        <span className={isBoard ? styles.boardText : null}>{displayText}</span>
      </a>
    );

    // 只有画板项需要 tooltip，发现和关注不需要
    if (isBoard) {
      return (
        <ConditionalTooltip
          key={option?.value || "all"}
          title={displayText}
          placement="top"
          mouseEnterDelay={0.3}
        >
          {itemElement}
        </ConditionalTooltip>
      );
    }

    return itemElement;
  };

  // 只有当文本溢出时才显示 tooltip 的组件
  const ConditionalTooltip = ({ children, title, ...tooltipProps }) => {
    const [isOverflowing, setIsOverflowing] = useState(false);
    const elementRef = useRef(null);

    // 检测文本是否溢出
    const checkOverflow = useCallback(() => {
      if (elementRef.current) {
        const textSpan = elementRef.current.querySelector(
          `.${styles.boardText}`
        );
        if (textSpan) {
          setIsOverflowing(textSpan.scrollWidth > textSpan.clientWidth);
        }
      }
    }, []);

    useEffect(() => {
      const timer = setTimeout(() => {
        checkOverflow();
      }, 0); // 延迟到下一个事件循环执行，确保DOM已渲染

      // 监听窗口大小变化
      window.addEventListener("resize", checkOverflow);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", checkOverflow);
      };
    }, [checkOverflow, title]);

    // 克隆子元素并添加ref
    const childWithRef = React.cloneElement(children, { ref: elementRef });

    // 只有当文本溢出时才显示 tooltip
    if (isOverflowing) {
      return (
        <Tooltip title={title} {...tooltipProps}>
          {childWithRef}
        </Tooltip>
      );
    }

    return childWithRef;
  };

  const renderAllItems = () => (
    <>
      <HBSwiper
        ref={swiperRef}
        className={styles.swiper}
        iconClassName={styles.swiperIcon}
        padding={20}
      >
        <When condition={!hideAll}>
          {renderItem(null)}
          {extraOption &&
            extraOption?.text &&
            extraOption?.value &&
            renderItem(extraOption, true)}
        </When>
        {/* 只有登录用户才显示画板列表和加载状态 */}
        {user && (
          <>
            {loading ? (
              <div className={styles.loading}>
                <HBIcon type="ic_loading" />
                {/* 加载中... */}
              </div>
            ) : (
              boards.map((option) => renderItem(option))
            )}
          </>
        )}
      </HBSwiper>
    </>
  );

  // 如果用户未登录，隐藏整个导航栏
  if (!user) {
    return null;
  }

  return (
    <div className={styles["category-nav"]}>
      <div className={styles.container}>{renderAllItems()}</div>
    </div>
  );
}
